import { nettoyerHtml } from '../utils/nettoyerHtml';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import CertificateFOAD from '../components/CertificateFOAD';
import {
  BookOpen, Play, CheckCircle, XCircle, Clock, ChevronRight,
  Lock, Award, FileText, BarChart3, ArrowLeft, Video,
  Image, File, ToggleLeft, HelpCircle, AlignLeft,
  Presentation, ExternalLink, Download, Upload,
  Gamepad2, Zap, Trophy, Hash, Star, Loader2, X, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============= INLINE KAHOOT PLAYER =============
const InlineKahoot = ({ user, onClose }) => {
  const [kahootStep, setKahootStep] = useState('join');
  const [gameCode, setGameCode] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [playerCount, setPlayerCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timer, setTimer] = useState(0);
  const [answerResult, setAnswerResult] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [error, setError] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const playerName = (user.prenom || '') + ' ' + (user.nom || '');

  useEffect(() => {
    return () => {
      if (wsRef.current) try { wsRef.current.close(); } catch {}
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const joinGame = () => {
    var code = gameCode.trim();
    if (!code) { setError('Entrez le code de la partie'); return; }
    setError('');
    try {
      var wsProtocol = BACKEND_URL.indexOf('https') === 0 ? 'wss' : 'ws';
      var wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
      var ws = new WebSocket(wsProtocol + '://' + wsHost + '/api/foad/kahoot/ws/player/' + code);
      wsRef.current = ws;
      // Client keepalive
      var keepaliveId = setInterval(function() { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'ping' })); }, 15000);
      ws.onopen = function() {
        var token = '';
        try { token = localStorage.getItem('token') || ''; } catch {}
        ws.send(JSON.stringify({ action: 'join', nickname: playerName.trim(), user_id: user.user_id, token: token }));
      };
      ws.onmessage = function(event) {
        try { var msg = JSON.parse(event.data); if (msg.type === 'ping') return; handleMsg(msg); } catch {}
      };
      ws.onerror = function() { setError('Erreur de connexion. Verifiez le code.'); };
      ws.onclose = function(e) { if (e.code !== 1000 && e.code !== 1001 && kahootStep !== 'finished') setError('Connexion perdue.'); };
    } catch { setError('Impossible de se connecter.'); }
  };

  const handleMsg = (msg) => {
    if (msg.type === 'error') { setError(msg.message); return; }
    if (msg.type === 'joined') { setPlayerId(msg.player_id); setPlayerCount(msg.player_count); setKahootStep(msg.game_status === 'playing' ? 'question' : 'waiting'); }
    if (msg.type === 'player_count') setPlayerCount(msg.count);
    if (msg.type === 'question') {
      setCurrentQuestion(msg); setQuestionIdx(msg.question_idx); setTotalQuestions(msg.total_questions);
      setAnswerResult(null); setKahootStep('question'); setQuestionStartTime(Date.now());
      setTimer(msg.time_limit);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(function() { setTimer(function(p) { if (p <= 1) { clearInterval(timerRef.current); return 0; } return p - 1; }); }, 1000);
    }
    if (msg.type === 'answer_confirmed') { setAnswerResult(msg); setTotalScore(msg.total_score); setKahootStep('answered'); if (timerRef.current) clearInterval(timerRef.current); }
    if (msg.type === 'game_over') { setLeaderboard(msg.leaderboard || []); setKahootStep('finished'); if (timerRef.current) clearInterval(timerRef.current); }
  };

  const submitAnswer = (sel) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ action: 'answer', selected: [sel], time_ms: Date.now() - (questionStartTime || Date.now()) }));
  };

  var colors = ['bg-red-500 active:bg-red-600', 'bg-blue-500 active:bg-blue-600', 'bg-amber-500 active:bg-amber-600', 'bg-green-500 active:bg-green-600'];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" data-testid="inline-kahoot">
      {/* JOIN */}
      {kahootStep === 'join' && (
        <div className="flex-1 bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="kahoot-close"><X className="h-6 w-6" /></button>
            <div className="text-center mb-6">
              <Gamepad2 className="h-12 w-12 text-white mx-auto mb-2" />
              <h1 className="text-2xl font-black text-white">Kahoot!</h1>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-2xl space-y-4">
              <div className="bg-violet-50 rounded-xl p-3 text-center">
                <p className="text-xs text-violet-500">Connecte en tant que</p>
                <p className="text-base font-bold text-violet-700" data-testid="kahoot-player-name">{playerName.trim()}</p>
              </div>
              {error && <p className="text-red-500 text-sm text-center font-medium bg-red-50 rounded-lg p-2">{error}</p>}
              <input value={gameCode} onChange={function(e) { setGameCode(e.target.value); }} placeholder="Code de la partie"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-4 text-center text-2xl font-bold tracking-widest focus:border-violet-500 outline-none"
                inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off"
                data-testid="kahoot-code-input"
                onKeyDown={function(e) { if (e.key === 'Enter') joinGame(); }} />
              <Button onClick={joinGame} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-4 text-base font-bold rounded-xl" data-testid="kahoot-join-btn">
                <Zap className="h-5 w-5 mr-2" /> Rejoindre
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WAITING */}
      {kahootStep === 'waiting' && (
        <div className="flex-1 bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4">
          <div className="text-center text-white">
            <div className="animate-bounce mb-6"><Gamepad2 className="h-16 w-16 mx-auto" /></div>
            <h2 className="text-2xl font-black mb-2">Vous etes connecte !</h2>
            <p className="text-violet-200 mb-2">{playerName.trim()}</p>
            <p className="text-violet-300 text-sm">En attente du formateur...</p>
            <p className="text-violet-200 mt-4 text-lg font-bold">{playerCount} joueur(s)</p>
            <Loader2 className="h-8 w-8 animate-spin mx-auto mt-6 text-violet-300" />
          </div>
        </div>
      )}

      {/* QUESTION */}
      {kahootStep === 'question' && currentQuestion && (
        <div className="flex-1 bg-slate-900 flex flex-col" data-testid="kahoot-question">
          <div className="p-3 flex items-center justify-between">
            <span className="text-white/60 text-sm font-medium">Q{questionIdx + 1}/{totalQuestions}</span>
            <div className={'flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg ' + (timer <= 3 ? 'bg-red-500 text-white animate-pulse' : 'bg-white/10 text-white')}>
              <Clock className="h-4 w-4" /> {timer}
            </div>
            <span className="text-violet-400 text-sm font-bold">{totalScore} pts</span>
          </div>
          <div className="px-4 py-4 text-center">
            <p className="text-white text-lg font-bold leading-relaxed">{currentQuestion.texte}</p>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-3 p-3">
            {(currentQuestion.options || []).map(function(o, i) {
              return (
                <button key={i} onClick={function() { submitAnswer(o.idx !== undefined ? o.idx : i); }}
                  className={colors[i % 4] + ' rounded-xl text-white font-bold text-lg flex items-center justify-center p-4 shadow-lg active:scale-95 transition-transform'}
                  data-testid={'kahoot-answer-' + i}>
                  {o.texte}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ANSWERED */}
      {kahootStep === 'answered' && (
        <div className={'flex-1 flex items-center justify-center p-4 ' + (answerResult && answerResult.correct ? 'bg-green-500' : 'bg-red-500')}>
          <div className="text-center text-white">
            {answerResult && answerResult.correct ? (
              <><CheckCircle className="h-20 w-20 mx-auto mb-4" /><h2 className="text-3xl font-black mb-2">Correct !</h2><p className="text-xl font-bold mb-1">+{answerResult.points_earned} pts</p></>
            ) : (
              <><XCircle className="h-20 w-20 mx-auto mb-4" /><h2 className="text-3xl font-black mb-2">Faux !</h2></>
            )}
            <p className="mt-6 text-white/80 text-lg font-bold">Score: {totalScore} pts</p>
            <Loader2 className="h-6 w-6 animate-spin mx-auto mt-6 text-white/50" />
          </div>
        </div>
      )}

      {/* FINISHED */}
      {kahootStep === 'finished' && (
        <div className="flex-1 bg-gradient-to-br from-violet-600 to-purple-700 p-4 overflow-y-auto">
          <div className="max-w-md mx-auto">
            <div className="text-center py-6">
              <Trophy className="h-14 w-14 text-amber-400 mx-auto mb-3" />
              <h1 className="text-3xl font-black text-white">Partie terminee !</h1>
            </div>
            {(() => {
              var myRank = leaderboard.findIndex(function(p) { return p.player_id === playerId; });
              var myData = myRank >= 0 ? leaderboard[myRank] : null;
              return myData ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white mb-6">
                  <p className="text-violet-200 text-sm mb-1">Votre resultat</p>
                  <p className="text-4xl font-black">{myRank + 1}{myRank === 0 ? 'er' : 'eme'}</p>
                  <p className="text-xl font-bold text-violet-200">{myData.score} points</p>
                  <p className="text-sm text-violet-300">{myData.correct_count}/{totalQuestions} bonnes reponses</p>
                </div>
              ) : null;
            })()}
            <div className="bg-white rounded-xl overflow-hidden mb-6">
              <div className="p-3 bg-slate-50 border-b"><h3 className="font-bold text-slate-700 text-sm flex items-center gap-2"><Hash className="h-4 w-4" /> Classement</h3></div>
              <div className="divide-y max-h-60 overflow-y-auto">
                {leaderboard.map(function(p, i) {
                  return (
                    <div key={p.player_id} className={'flex items-center justify-between px-3 py-2.5 ' + (p.player_id === playerId ? 'bg-violet-50' : '')}>
                      <div className="flex items-center gap-2.5">
                        <span className={'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ' + (i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600')}>{i + 1}</span>
                        <div>
                          <p className="font-medium text-sm">{p.nickname} {p.player_id === playerId && <Star className="h-3 w-3 inline text-amber-500" />}</p>
                          <p className="text-xs text-slate-400">{p.correct_count} bonnes - {p.avg_time || 0}s/q</p>
                        </div>
                      </div>
                      <span className="font-bold text-violet-600 text-sm">{p.score}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <Button onClick={onClose} className="w-full bg-white text-violet-700 hover:bg-violet-50 font-bold py-3 rounded-xl" data-testid="kahoot-close-btn">
              Fermer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


const FOADApprenantPage = ({ user, isImpersonating = false }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showKahoot, setShowKahoot] = useState(false);
  const [certData, setCertData] = useState(null);
  const heartbeatRef = useRef(null);

  // Activity tracking: heartbeat every 30s when a module is active
  useEffect(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (selectedCourse && activeModule) {
      const sendHeartbeat = () => {
        axios.post('/foad/heartbeat', {
          course_id: selectedCourse.course_id,
          module_id: activeModule.module_id
        }).catch(() => {});
      };
      sendHeartbeat(); // immediate first beat
      heartbeatRef.current = setInterval(sendHeartbeat, 30000);
    }
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [selectedCourse, activeModule]);

  const loadCourses = useCallback(async () => {
    try {
      const params = isImpersonating ? `?as_user=${user?.user_id}` : '';
      const r = await axios.get(`/foad/courses${params}`);
      // Filter to only courses the user is enrolled in
      const enrolled = r.data.filter(c =>
        (c.stagiaires_inscrits || []).includes(user?.user_id)
      );
      setCourses(enrolled);
    } catch { toast.error('Erreur chargement cours'); }
    finally { setLoading(false); }
  }, [user, isImpersonating]);

  const loadCourseDetail = useCallback(async (courseId) => {
    try {
      const params = isImpersonating ? `?as_user=${user?.user_id}` : '';
      const r = await axios.get(`/foad/courses/${courseId}${params}`);
      setCourseDetail(r.data);
      // Auto start progress (only if not impersonating to avoid creating progress for wrong user)
      if (!isImpersonating) {
        await axios.post(`/foad/courses/${courseId}/start`).catch(() => {});
      }
    } catch { toast.error('Erreur chargement cours'); }
  }, [isImpersonating, user]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const openCourse = (course) => {
    setSelectedCourse(course);
    loadCourseDetail(course.course_id);
    setActiveModule(null);
    setQuizResult(null);
    setQuizAnswers({});
    // Log connexion for Qualiopi tracking
    axios.post('/foad/connexion-log', { course_id: course.course_id }).catch(() => {});
  };

  const openModule = async (module) => {
    setActiveModule(module);
    setQuizResult(null);
    setQuizAnswers({});
    // Mark non-quiz modules as complete immediately
    if (module.type !== 'quiz' && courseDetail) {
      try {
        await axios.post(`/foad/courses/${courseDetail.course_id}/modules/${module.module_id}/complete`);
        loadCourseDetail(courseDetail.course_id);
      } catch {}
    }
  };

  const isModuleCompleted = (moduleId) => {
    const prog = courseDetail?.progress;
    if (!prog) return false;
    return (prog.modules_completed || []).some(m => m.module_id === moduleId);
  };

  const getQuizBestScore = (moduleId) => {
    const prog = courseDetail?.progress;
    if (!prog) return null;
    const attempts = (prog.quiz_attempts || []).filter(a => a.module_id === moduleId);
    if (attempts.length === 0) return null;
    return Math.max(...attempts.map(a => a.note_sur_10 || 0));
  };

  const getQuizAttempts = (moduleId) => {
    const prog = courseDetail?.progress;
    if (!prog) return 0;
    return (prog.quiz_attempts || []).filter(a => a.module_id === moduleId).length;
  };

  const isModuleBlocked = (module, idx) => {
    // Check date d'ouverture du module
    if (module?.date_ouverture) {
      const openStr = module.heure_ouverture
        ? `${module.date_ouverture}T${module.heure_ouverture}`
        : `${module.date_ouverture}T00:00`;
      if (new Date() < new Date(openStr)) return 'date'; // blocked by date
    }
    if (idx === 0) return false;
    const modules = courseDetail?.modules || [];
    // Skip chapitre modules (they are section headers, not completable content)
    let prevIdx = idx - 1;
    while (prevIdx >= 0 && modules[prevIdx]?.type === 'chapitre') {
      prevIdx--;
    }
    if (prevIdx < 0) return false; // Only chapitres before this module
    const prevModule = modules[prevIdx];
    if (prevModule.type === 'quiz' && prevModule.quiz_bloquant) {
      const best = getQuizBestScore(prevModule.module_id);
      const passScore = prevModule.note_passage || 5;
      if (best === null || best < passScore) return true;
    }
    if (prevModule.type === 'devoir' && prevModule.bloquant) {
      if (!isModuleCompleted(prevModule.module_id)) return true;
    }
    return !isModuleCompleted(prevModule.module_id) ? true : false;
  };

  const submitQuiz = async () => {
    if (!activeModule || !courseDetail) return;
    setSubmitting(true);
    try {
      const questions = activeModule.questions || [];
      const formattedAnswers = questions.map(q => {
        const ans = quizAnswers[q.question_id] || {};
        return {
          question_id: q.question_id,
          selected: ans.selected || [],
          text: ans.text || '',
          blanks: ans.blanks || [],
          pairs: ans.pairs || []
        };
      });
      const r = await axios.post(
        `/foad/courses/${courseDetail.course_id}/modules/${activeModule.module_id}/submit-quiz`,
        formattedAnswers
      );
      setQuizResult(r.data);
      // Refresh course detail
      loadCourseDetail(courseDetail.course_id);
      if (r.data.reussi) {
        // Mark quiz as completed
        await axios.post(`/foad/courses/${courseDetail.course_id}/modules/${activeModule.module_id}/complete`).catch(() => {});
        loadCourseDetail(courseDetail.course_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur soumission quiz');
    } finally { setSubmitting(false); }
  };

  // ============= COURSES LIST =============
  if (!selectedCourse) {
    return (
      <div className="space-y-6" data-testid="apprenant-courses">
        {showKahoot && <InlineKahoot user={user} onClose={() => setShowKahoot(false)} />}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <BookOpen className="h-7 w-7 text-blue-600" /> Mes Formations
            </h1>
            <p className="text-slate-500 text-sm mt-1">Retrouvez vos cours et suivez votre progression</p>
          </div>
          <Button onClick={() => setShowKahoot(true)} className="bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl px-4 py-2.5" data-testid="open-kahoot-btn">
            <Gamepad2 className="h-4 w-4 mr-2" /> Rejoindre un Kahoot
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>
        ) : courses.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Aucun cours disponible</p>
            <p className="text-slate-400 text-sm mt-1">Vous n'etes inscrit a aucun cours pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map(c => {
              const prog = c.progress?.percentage || 0;
              const certReady = c.progress?.certificate_generated;
              return (
                <div key={c.course_id} onClick={() => openCourse(c)}
                  className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`apprenant-course-${c.course_id}`}>
                  {c.image_couverture || c.coverImageUrl ? (
                    <div className="h-36 bg-slate-200 overflow-hidden">
                      <img src={c.image_couverture || c.coverImageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  ) : (
                    <div className="h-36 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-white/40" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-bold text-slate-800 mb-1 group-hover:text-blue-600 transition-colors">{c.titre}</h3>
                    {c.formateur_nom && <p className="text-xs text-slate-500 mb-2">par {c.formateur_nom}</p>}
                    {c.duree_estimee && <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {c.duree_estimee}</p>}
                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Progression</span>
                        <span className="font-bold text-blue-600">{prog}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${prog >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${prog}%` }} />
                      </div>
                    </div>
                    {certReady && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-green-600 font-medium">
                        <Award className="h-3.5 w-3.5" /> Certificat disponible
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ============= COURSE VIEW with SIDEBAR + MODULE =============
  const modules = courseDetail?.modules || [];
  const progress = courseDetail?.progress;
  const certReady = progress?.certificate_generated;

  // Build chapter hierarchy
  const buildChapterTree = () => {
    const tree = [];
    let currentChapter = null;
    modules.forEach((m, idx) => {
      if (m.type === 'chapitre') {
        currentChapter = { ...m, idx, children: [] };
        tree.push(currentChapter);
      } else {
        if (currentChapter) {
          currentChapter.children.push({ ...m, idx });
        } else {
          tree.push({ ...m, idx, children: null });
        }
      }
    });
    return tree;
  };
  const chapterTree = buildChapterTree();

  const currentModuleIdx = activeModule ? modules.findIndex(m => m.module_id === activeModule.module_id) : -1;
  const nextModule = currentModuleIdx >= 0 && currentModuleIdx < modules.length - 1 ? modules[currentModuleIdx + 1] : null;
  const prevModule = currentModuleIdx > 0 ? modules[currentModuleIdx - 1] : null;

  const goToModule = (mod, idx) => {
    if (!isModuleBlocked(mod, idx)) {
      openModule(mod);
    }
  };

  // Sidebar
  const Sidebar = () => (
    <div className="w-80 flex-shrink-0 bg-white border-r overflow-y-auto max-h-[calc(100vh-120px)] hidden lg:block" data-testid="course-sidebar">
      <div className="p-4 border-b bg-slate-50">
        <button onClick={() => { if (activeModule) { setActiveModule(null); setQuizResult(null); setQuizAnswers({}); } else { setSelectedCourse(null); setCourseDetail(null); } }}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-blue-600 mb-2" data-testid="sidebar-back">
          <ArrowLeft className="h-3 w-3" /> {activeModule ? 'Sommaire' : 'Mes formations'}
        </button>
        <h3 className="font-bold text-sm text-slate-800 line-clamp-2">{selectedCourse.titre}</h3>
        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${(progress?.percentage || 0) >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progress?.percentage || 0}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-1">{progress?.percentage || 0}% termine</p>
      </div>
      <div className="py-2">
        {chapterTree.map((item) => {
          if (item.children !== null && item.children !== undefined) {
            // Chapter with children
            const chapCompleted = item.children.every(c => isModuleCompleted(c.module_id));
            return (
              <div key={item.module_id}>
                <div className={`px-4 py-2.5 flex items-center gap-2 ${chapCompleted ? 'text-green-700' : 'text-slate-700'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold ${chapCompleted ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                    {chapCompleted ? <CheckCircle className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wide">{item.titre}</span>
                </div>
                {item.contenu && (
                  <div className="mx-4 mb-2 rounded overflow-hidden h-10 w-16 flex-shrink-0">
                    <img src={item.contenu} alt="" className="w-full h-full object-cover rounded" />
                  </div>
                )}
                {item.children.map(child => {
                  const isActive = activeModule?.module_id === child.module_id;
                  const completed = isModuleCompleted(child.module_id);
                  const blocked = isModuleBlocked(child, child.idx);
                  return (
                    <button key={child.module_id} onClick={() => goToModule(child, child.idx)} disabled={!!blocked}
                      className={`w-full text-left pl-10 pr-4 py-2 flex items-center gap-2 text-xs transition-colors
                        ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600' : ''}
                        ${blocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}
                        ${completed && !isActive ? 'text-green-600' : !isActive ? 'text-slate-600' : ''}`}
                      title={blocked === 'date' ? `Ouverture le ${new Date(child.date_ouverture).toLocaleDateString('fr-FR')}` : ''}>
                      {blocked === 'date' ? <Clock className="h-3 w-3 flex-shrink-0 text-blue-400" /> :
                       blocked ? <Lock className="h-3 w-3 flex-shrink-0" /> :
                       completed ? <CheckCircle className="h-3 w-3 flex-shrink-0" /> :
                       <Play className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate">{child.titre}</span>
                      {blocked === 'date' && child.date_ouverture && <span className="text-[10px] text-blue-400 ml-auto flex-shrink-0">{new Date(child.date_ouverture).toLocaleDateString('fr-FR')}</span>}
                    </button>
                  );
                })}
              </div>
            );
          } else {
            // Standalone module
            const isActive = activeModule?.module_id === item.module_id;
            const completed = isModuleCompleted(item.module_id);
            const blocked = isModuleBlocked(item, item.idx);
            return (
              <button key={item.module_id} onClick={() => goToModule(item, item.idx)} disabled={!!blocked}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-2 text-xs transition-colors
                  ${isActive ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600' : ''}
                  ${blocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}
                  ${completed && !isActive ? 'text-green-600' : !isActive ? 'text-slate-600' : ''}`}
                title={blocked === 'date' ? `Ouverture le ${new Date(item.date_ouverture).toLocaleDateString('fr-FR')}` : ''}>
                {blocked === 'date' ? <Clock className="h-3 w-3 flex-shrink-0 text-blue-400" /> :
                 blocked ? <Lock className="h-3 w-3 flex-shrink-0" /> :
                 completed ? <CheckCircle className="h-3 w-3 flex-shrink-0" /> :
                 <Play className="h-3 w-3 flex-shrink-0" />}
                <span className="truncate">{item.titre}</span>
                {blocked === 'date' && item.date_ouverture && <span className="text-[10px] text-blue-400 ml-auto flex-shrink-0">{new Date(item.date_ouverture).toLocaleDateString('fr-FR')}</span>}
              </button>
            );
          }
        })}
      </div>
    </div>
  );

  // Course overview (no module selected)
  if (!activeModule) {
    return (
      <div className="flex gap-0 -mx-4 -mt-4" data-testid="apprenant-course-view">
        <Sidebar />
        <div className="flex-1 p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-120px)]">
          <button onClick={() => { setSelectedCourse(null); setCourseDetail(null); }}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 lg:hidden" data-testid="back-to-courses-mobile">
            <ArrowLeft className="h-4 w-4" /> Mes formations
          </button>

          {/* Course header */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {(selectedCourse.image_couverture || selectedCourse.coverImageUrl) && (
              <div className="h-48 bg-slate-200 overflow-hidden">
                <img src={selectedCourse.image_couverture || selectedCourse.coverImageUrl} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6">
              <h1 className="text-2xl font-bold text-slate-800">{selectedCourse.titre}</h1>
              {selectedCourse.formateur_nom && <p className="text-sm text-slate-500 mt-1">par {selectedCourse.formateur_nom}</p>}
              {selectedCourse.description && <p className="text-sm text-slate-600 mt-3 whitespace-pre-line">{selectedCourse.description}</p>}
              <div className="flex items-center gap-6 mt-4">
                <span className="text-sm text-slate-500 flex items-center gap-1.5"><BarChart3 className="h-4 w-4" /> {progress?.percentage || 0}% termine</span>
                {selectedCourse.duree_estimee && <span className="text-sm text-slate-500 flex items-center gap-1.5"><Clock className="h-4 w-4" /> {selectedCourse.duree_estimee}</span>}
                {modules.length > 0 && <span className="text-sm text-slate-500">{modules.length} module(s)</span>}
              </div>
              <div className="mt-4 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${(progress?.percentage || 0) >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progress?.percentage || 0}%` }} />
              </div>
            </div>
          </div>

          {/* Certificate */}
          {certReady && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between" data-testid="certificate-banner">
              <div className="flex items-center gap-3">
                <Award className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-bold text-green-800">Felicitations ! Votre certificat est pret</p>
                  <p className="text-xs text-green-600">Vous avez reussi tous les quiz de ce cours</p>
                </div>
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                onClick={async () => {
                  try {
                    const res = await axios.get(`/foad/courses/${courseDetail.course_id}/certificate/${user?.user_id}`);
                    const cert = res.data;
                    const nameParts = (cert.user_nom || '').split(' ');
                    const prenom = nameParts[0] || '';
                    const nom = nameParts.slice(1).join(' ') || '';
                    const duree = cert.duree_estimee ? (String(cert.duree_estimee).match(/heure|h|min/i) ? cert.duree_estimee : cert.duree_estimee + ' heures') : '';
                    const nbMods = (courseDetail?.modules || []).filter(m => m.type !== 'chapitre').length;
                    let tamponImg = '', gerantNom = '';
                    try { const s = await axios.get('/foad/centre-stamp'); tamponImg = s.data.tampon_img || ''; gerantNom = s.data.gerant_nom || ''; } catch {}
                    setCertData({
                      prenom, nom,
                      courseTitre: cert.course_titre || '',
                      courseDescription: selectedCourse?.description || '',
                      objectifs: cert.objectifs || '',
                      dureeEstimee: duree,
                      noteSur10: (cert.score / 10).toFixed(1),
                      nbModules: nbMods,
                      dateStr: new Date(cert.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
                      refNumber: `${courseDetail.course_id.substring(0, 8).toUpperCase()}`,
                      tamponImg, gerantNom
                    });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Erreur lors de la generation du certificat');
                  }
                }}
                data-testid="download-certificate-btn">
                <Download className="h-4 w-4 mr-1" /> Telecharger
              </Button>
            </div>
          )}
          {certData && <CertificateFOAD data={certData} onClose={() => setCertData(null)} />}

          {/* Modules as chapter cards */}
          <div className="space-y-4">
            {chapterTree.map((item) => {
              if (item.children !== null && item.children !== undefined) {
                return (
                  <div key={item.module_id} className="bg-white rounded-xl border overflow-hidden">
                    {item.contenu && (
                      <div className="h-32 bg-slate-200 overflow-hidden">
                        <img src={item.contenu} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4 bg-slate-50 border-b">
                      <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-blue-600" /> {item.titre}
                      </h3>
                      {item.description && <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">{item.description}</p>}
                    </div>
                    <div className="divide-y">
                      {item.children.map(child => {
                        const completed = isModuleCompleted(child.module_id);
                        const blocked = isModuleBlocked(child, child.idx);
                        const bestScore = getQuizBestScore(child.module_id);
                        const attempts = getQuizAttempts(child.module_id);
                        const isQuiz = child.type === 'quiz';
                        const quizPassed = bestScore !== null && bestScore >= (child.note_passage || 5);
                        return (
                          <button key={child.module_id} onClick={() => goToModule(child, child.idx)} disabled={!!blocked}
                            className={`w-full text-left p-3 flex items-center gap-3 transition-colors ${blocked ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'hover:bg-blue-50 cursor-pointer'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${completed || quizPassed ? 'bg-green-100' : blocked === 'date' ? 'bg-blue-100' : blocked ? 'bg-slate-200' : 'bg-blue-100'}`}>
                              {blocked === 'date' ? <Clock className="h-3.5 w-3.5 text-blue-500" /> :
                               blocked ? <Lock className="h-3.5 w-3.5 text-slate-400" /> :
                               completed || quizPassed ? <CheckCircle className="h-4 w-4 text-green-600" /> :
                               isQuiz ? <HelpCircle className="h-4 w-4 text-blue-600" /> :
                               child.type === 'video' ? <Video className="h-4 w-4 text-blue-600" /> :
                               child.type === 'devoir' ? <FileText className="h-4 w-4 text-blue-600" /> :
                               <Play className="h-4 w-4 text-blue-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-800">{child.titre}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                <span>{isQuiz ? 'Quiz' : child.type === 'video' ? 'Video' : child.type === 'devoir' ? 'Devoir' : 'Texte'}</span>
                                {isQuiz && bestScore !== null && <span className={quizPassed ? 'text-green-600' : 'text-red-500'}>{bestScore}/10 ({attempts} essai{attempts > 1 ? 's' : ''})</span>}
                                {blocked === 'date' && child.date_ouverture && <span className="text-blue-500">Ouvre le {new Date(child.date_ouverture).toLocaleDateString('fr-FR')}</span>}
                              </div>
                            </div>
                            {!blocked && <ChevronRight className="h-4 w-4 text-slate-300" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                const completed = isModuleCompleted(item.module_id);
                const blocked = isModuleBlocked(item, item.idx);
                return (
                  <button key={item.module_id} onClick={() => goToModule(item, item.idx)} disabled={!!blocked}
                    className={`w-full bg-white rounded-xl border p-4 text-left flex items-center gap-3 transition-colors ${blocked ? 'opacity-50' : 'hover:bg-blue-50 cursor-pointer'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${completed ? 'bg-green-100' : blocked === 'date' ? 'bg-blue-50' : 'bg-blue-100'}`}>
                      {blocked === 'date' ? <Clock className="h-5 w-5 text-blue-500" /> :
                       blocked ? <Lock className="h-5 w-5 text-slate-400" /> :
                       completed ? <CheckCircle className="h-5 w-5 text-green-600" /> : <Play className="h-5 w-5 text-blue-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.titre}</p>
                      <span className="text-xs text-slate-500">{item.type === 'quiz' ? 'Quiz' : item.type === 'video' ? 'Video' : 'Texte'}</span>
                      {blocked === 'date' && item.date_ouverture && <span className="text-xs text-blue-500 ml-2">Ouvre le {new Date(item.date_ouverture).toLocaleDateString('fr-FR')}</span>}
                    </div>
                    {!blocked && <ChevronRight className="h-5 w-5 text-slate-300" />}
                  </button>
                );
              }
            })}
          </div>
        </div>
      </div>
    );
  }

  // ============= MODULE VIEW with SIDEBAR =============
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      const params = new URLSearchParams(url.split('?')[1] || '');
      videoId = params.get('v') || '';
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0] || '';
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  return (
    <div className="flex gap-0 -mx-4 -mt-4 overflow-x-hidden" data-testid="apprenant-module-view">
      <Sidebar />
      <div className="flex-1 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-120px)] min-w-0">
        {/* Mobile back */}
        <div className="lg:hidden p-4 border-b bg-white">
          <button onClick={() => { setActiveModule(null); setQuizResult(null); setQuizAnswers({}); }}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600" data-testid="back-to-modules-mobile">
            <ArrowLeft className="h-4 w-4" /> Retour au sommaire
          </button>
        </div>

        <div className="p-3 sm:p-6">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-3 sm:p-6 border-b">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800">{activeModule.titre}</h2>
              <span className="text-xs text-slate-500 mt-1 capitalize">{activeModule.type === 'quiz' ? 'Quiz' : activeModule.type === 'video' ? 'Video' : activeModule.type === 'devoir' ? 'Devoir' : activeModule.type}</span>
            </div>

            <div className="p-3 sm:p-6">
              {/* CHAPTER */}
              {activeModule.type === 'chapitre' && (
                <div>
                  {activeModule.contenu && <img src={activeModule.contenu} alt="" className="w-full max-h-80 object-cover rounded-xl mb-4" />}
                  {activeModule.description && <p className="text-slate-600 whitespace-pre-line">{activeModule.description}</p>}
                </div>
              )}

              {/* TEXT CONTENT */}
              {activeModule.type === 'texte' && (
                <div className="prose prose-slate sm:prose-lg max-w-none break-words overflow-hidden [&_img]:max-w-full [&_img]:h-auto [&_iframe]:max-w-full [&_table]:max-w-full [&_table]:overflow-x-auto [&_pre]:overflow-x-auto [&_pre]:max-w-full" data-testid="module-texte-content"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  dangerouslySetInnerHTML={{ __html: nettoyerHtml(activeModule.contenu || '<p>Pas de contenu</p>') }} />
              )}

              {/* VIDEO with fixed YouTube embed */}
              {activeModule.type === 'video' && activeModule.video_url && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden" data-testid="module-video">
                  {activeModule.video_url.includes('youtube') || activeModule.video_url.includes('youtu.be') ? (
                    <iframe src={getYouTubeEmbedUrl(activeModule.video_url)}
                      className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      title={activeModule.titre} />
                  ) : (
                    <video src={activeModule.video_url} controls className="w-full h-full" />
                  )}
                </div>
              )}

              {/* PDF */}
              {activeModule.type === 'pdf' && activeModule.fichier_url && (
                <div data-testid="module-pdf">
                  <iframe src={activeModule.fichier_url} className="w-full h-[70vh] rounded-lg border" title={activeModule.titre} />
                  <a href={activeModule.fichier_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un nouvel onglet
                  </a>
                </div>
              )}

              {/* IMAGE */}
              {activeModule.type === 'image' && activeModule.contenu && (
                <div data-testid="module-image" className="text-center">
                  <img src={activeModule.contenu} alt={activeModule.titre} className="max-w-full rounded-lg mx-auto" />
                </div>
              )}

              {/* GOOGLE SLIDES */}
              {activeModule.type === 'google_slides' && activeModule.embed_code && (
                <div data-testid="module-slides" className="overflow-hidden">
                  <div className="rounded-lg overflow-hidden" style={{ position: 'relative', paddingBottom: '75%', height: 0 }}
                    dangerouslySetInnerHTML={{ __html: nettoyerHtml(activeModule.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"').replace(/<iframe/g, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"')) }} />
                </div>
              )}

              {/* GOOGLE DOCS / SHEETS */}
              {activeModule.type === 'google_docs' && (
                <div data-testid="module-gdocs">
                  {activeModule.embed_code ? (
                    <div className="rounded-lg overflow-hidden border" style={{ height: '70vh' }}
                      dangerouslySetInnerHTML={{ __html: nettoyerHtml(activeModule.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"')) }} />
                  ) : activeModule.contenu ? (
                    <div className="rounded-lg overflow-hidden border" style={{ height: '70vh' }}>
                      <iframe src={activeModule.contenu.includes('/pub') ? activeModule.contenu : activeModule.contenu.replace(/\/edit.*$/, '/pub?embedded=true')}
                        className="w-full h-full border-0" title={activeModule.titre || 'Google Document'} />
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">Aucun document Google configure</p>
                  )}
                </div>
              )}

              {/* DOCUMENT PDF/WORD */}
              {activeModule.type === 'document' && (
                <div data-testid="module-document">
                  {activeModule.contenu && activeModule.contenu.startsWith('doc:') && (() => {
                    const access = activeModule.document_access || 'consulter';
                    const docUrl = `${process.env.REACT_APP_BACKEND_URL}/api/foad/documents/${activeModule.contenu.replace('doc:', '')}?token=${localStorage.getItem('token')}`;
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                        <p className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><FileText className="h-5 w-5 text-amber-600" />{activeModule.description || 'Document'}</p>
                        {(access === 'consulter' || access === 'les_deux') && (
                          <iframe src={`${docUrl}#toolbar=0`} className="w-full rounded-lg border" style={{ height: '70vh', pointerEvents: access === 'consulter' ? 'auto' : 'auto' }} title={activeModule.description || 'Document'} />
                        )}
                        {(access === 'telecharger' || access === 'les_deux') && (
                          <a href={docUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium mt-3"
                            data-testid="doc-download-btn">
                            <Download className="h-4 w-4" /> Telecharger le document
                          </a>
                        )}
                        {access === 'consulter' && (
                          <p className="text-xs text-slate-400 mt-3 text-center">Ce document est en consultation uniquement</p>
                        )}
                      </div>
                    );
                  })()}
                  {activeModule.embed_code && (
                    <div className="rounded-lg overflow-hidden" style={{ position: 'relative', paddingBottom: '75%', height: 0 }}
                      dangerouslySetInnerHTML={{ __html: nettoyerHtml(activeModule.embed_code.replace(/width="[^"]*"/g, 'width="100%"').replace(/height="[^"]*"/g, 'height="100%"').replace(/<iframe/g, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"')) }} />
                  )}
                  {!activeModule.contenu && !activeModule.embed_code && (
                    <p className="text-slate-400 text-center py-8">Aucun document configure</p>
                  )}
                </div>
              )}

              {/* DEVOIR */}
              {activeModule.type === 'devoir' && (
                <DevoirStudentView module={activeModule} courseId={courseDetail?.course_id} userId={user?.user_id} />
              )}

              {/* QUIZ */}
              {activeModule.type === 'quiz' && (
                <QuizView module={activeModule} courseId={courseDetail?.course_id} answers={quizAnswers} setAnswers={setQuizAnswers}
                  result={quizResult} onSubmit={submitQuiz} submitting={submitting}
                  attempts={getQuizAttempts(activeModule.module_id)} bestScore={getQuizBestScore(activeModule.module_id)} />
              )}
            </div>
          </div>

          {/* NAVIGATION: Previous / Next */}
          <div className="flex items-center justify-between mt-6 pb-6">
            {prevModule ? (
              <Button variant="outline" onClick={() => goToModule(prevModule, currentModuleIdx - 1)} className="flex items-center gap-2" data-testid="prev-module-btn">
                <ArrowLeft className="h-4 w-4" /> Precedent
              </Button>
            ) : <div />}
            {nextModule ? (
              <Button onClick={() => goToModule(nextModule, currentModuleIdx + 1)}
                disabled={isModuleBlocked(nextModule, currentModuleIdx + 1)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="next-module-btn">
                Suivant <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="outline" onClick={() => { setActiveModule(null); setQuizResult(null); setQuizAnswers({}); }} className="flex items-center gap-2" data-testid="finish-course-btn">
                Terminer <CheckCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ============= QUIZ VIEW COMPONENT =============
const QuizView = ({ module, courseId, answers, setAnswers, result, onSubmit, submitting, attempts, bestScore }) => {
  const questions = module.questions || [];
  const maxAttempts = module.tentatives_quiz || 3;
  const passScore = module.note_passage || 5;
  const canRetry = attempts < maxAttempts;

  const setAnswer = (qid, data) => {
    setAnswers(prev => ({ ...prev, [qid]: { ...prev[qid], ...data } }));
  };

  // Quiz result view
  if (result) {
    return (
      <div className="space-y-6" data-testid="quiz-result">
        <div className={`text-center p-6 rounded-xl ${result.reussi ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {result.reussi ? (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-800">Quiz reussi !</h3>
            </>
          ) : (
            <>
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-red-800">Quiz non reussi</h3>
            </>
          )}
          <p className="text-2xl font-black mt-2">{result.note_sur_10}/10</p>
          <p className="text-sm text-slate-500 mt-1">Note de passage: {result.note_passage}/10</p>
        </div>

        {/* Results per question */}
        <div className="space-y-3">
          {questions.map((q, qi) => {
            const r = result.results?.[qi];
            const userAnswer = r?.user_answer;
            return (
              <div key={q.question_id} className={`p-4 rounded-lg border ${r?.correct ? 'bg-green-50 border-green-200' : r?.correct === null ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {r?.correct ? <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" /> : r?.correct === null ? <HelpCircle className="h-5 w-5 text-amber-500 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 mt-0.5" />}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      <span className="text-blue-600 mr-1">Q{qi + 1}.</span>
                      {q.texte}
                    </p>
                    {/* Réponse du candidat */}
                    {q.type === 'ouverte' ? (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Votre réponse :</p>
                        <p className="text-sm bg-white border border-slate-200 rounded px-3 py-2 text-slate-700">{userAnswer || r?.reponse_stagiaire || '—'}</p>
                      </div>
                    ) : q.options && (
                      <div className="mt-2 space-y-1">
                        {q.options.map((o, oi) => {
                          const isUserChoice = Array.isArray(userAnswer) ? userAnswer.includes(oi) : (r?.selected || []).includes(oi);
                          const isCorrect = o.correct;
                          return (
                            <div key={oi} className={`text-xs px-3 py-1.5 rounded flex items-center gap-2 ${
                              isCorrect && isUserChoice ? 'bg-green-200 text-green-800 font-semibold' :
                              isCorrect ? 'bg-green-100 text-green-700' :
                              isUserChoice ? 'bg-red-200 text-red-800' :
                              'text-slate-500 bg-slate-50'
                            }`}>
                              {isUserChoice && isCorrect && <CheckCircle className="h-3 w-3" />}
                              {isUserChoice && !isCorrect && <XCircle className="h-3 w-3" />}
                              {!isUserChoice && isCorrect && <span className="text-green-500 text-xs">&#10003;</span>}
                              <span>{o.texte}</span>
                              {isUserChoice && <span className="ml-auto text-[10px] opacity-70">(votre réponse)</span>}
                              {isCorrect && <span className="ml-auto text-[10px] opacity-70">(bonne réponse)</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!result.reussi && canRetry && (
          <div className="text-center">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setAnswers({}); }} data-testid="retry-quiz-btn">
              Reessayer ({attempts}/{maxAttempts} tentatives)
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Quiz form
  return (
    <div className="space-y-6" data-testid="quiz-form">
      {bestScore !== null && (
        <div className="bg-slate-50 rounded-lg p-3 text-center text-sm">
          Meilleure note: <span className="font-bold text-blue-600">{bestScore}/10</span>
          {' '}| Tentative {attempts + 1}/{maxAttempts}
          {!canRetry && <span className="text-red-500 ml-2">Nombre max de tentatives atteint</span>}
        </div>
      )}

      {!canRetry ? (
        <div className="text-center py-8 text-slate-500">
          <Lock className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Vous avez utilise toutes vos tentatives</p>
          <p className="text-sm mt-1">Meilleure note: {bestScore}/10</p>
        </div>
      ) : (
        <>
          {questions.map((q, qi) => (
            <div key={q.question_id} className="border rounded-lg p-4" data-testid={`quiz-q-${qi}`}>
              <p className="font-medium text-slate-800 mb-3">
                <span className="text-blue-600 mr-2">Q{qi + 1}.</span>
                {q.texte}
              </p>

              {/* QCM / Vrai-Faux */}
              {(q.type === 'qcm' || q.type === 'vrai_faux') && (
                <div className="space-y-2">
                  {(q.options || []).map((o, oi) => {
                    const selected = (answers[q.question_id]?.selected || []).includes(oi);
                    return (
                      <button key={oi} onClick={() => setAnswer(q.question_id, { selected: [oi] })}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300 text-slate-700'}`}
                        data-testid={`quiz-q${qi}-opt${oi}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                            {selected && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                          {o.texte}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* QCM Multiple */}
              {q.type === 'qcm_multiple' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 mb-1">Plusieurs reponses possibles</p>
                  {(q.options || []).map((o, oi) => {
                    const selected = (answers[q.question_id]?.selected || []).includes(oi);
                    return (
                      <button key={oi} onClick={() => {
                        const cur = answers[q.question_id]?.selected || [];
                        const next = selected ? cur.filter(x => x !== oi) : [...cur, oi];
                        setAnswer(q.question_id, { selected: next });
                      }}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300 text-slate-700'}`}
                        data-testid={`quiz-q${qi}-opt${oi}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${selected ? 'bg-blue-500' : 'border-2 border-slate-300'}`}>
                            {selected && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          {o.texte}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Texte a trou */}
              {q.type === 'texte_trou' && (
                <div className="space-y-2">
                  {(q.blanks || []).map((_, bi) => (
                    <input key={bi} placeholder={`Reponse ${bi + 1}...`}
                      value={(answers[q.question_id]?.blanks || [])[bi] || ''}
                      onChange={e => {
                        const blanks = [...(answers[q.question_id]?.blanks || [])];
                        blanks[bi] = e.target.value;
                        setAnswer(q.question_id, { blanks });
                      }}
                      className="w-full border-2 border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none"
                      data-testid={`quiz-q${qi}-blank${bi}`} />
                  ))}
                </div>
              )}

              {/* Appariement */}
              {q.type === 'appariement' && (
                <div className="space-y-2">
                  {(q.pairs || []).map((p, pi) => (
                    <div key={pi} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-700 w-1/3">{p.left}</span>
                      <span className="text-slate-400">→</span>
                      <input placeholder="Reponse..."
                        value={(answers[q.question_id]?.pairs || [])[pi]?.right || ''}
                        onChange={e => {
                          const pairs = [...(answers[q.question_id]?.pairs || q.pairs.map(pp => ({ left: pp.left, right: '' })))];
                          pairs[pi] = { left: p.left, right: e.target.value };
                          setAnswer(q.question_id, { pairs });
                        }}
                        className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none"
                        data-testid={`quiz-q${qi}-pair${pi}`} />
                    </div>
                  ))}
                </div>
              )}

              {/* Question ouverte */}
              {q.type === 'ouverte' && (
                <textarea placeholder="Votre reponse..."
                  value={answers[q.question_id]?.text || ''}
                  onChange={e => setAnswer(q.question_id, { text: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-4 py-3 text-sm h-24 focus:border-blue-500 outline-none resize-none"
                  data-testid={`quiz-q${qi}-text`} />
              )}
            </div>
          ))}

          <div className="text-center pt-4">
            <Button onClick={onSubmit} className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 text-lg font-bold" disabled={submitting} data-testid="submit-quiz-btn">
              {submitting ? 'Envoi en cours...' : 'Soumettre le quiz'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ============= DEVOIR STUDENT VIEW =============
const DevoirStudentView = ({ module, courseId, userId }) => {
  const [submission, setSubmission] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (courseId && module?.module_id) {
      axios.get(`/foad/courses/${courseId}/modules/${module.module_id}/my-submission`)
        .then(r => { setSubmission(r.data); setLoaded(true); })
        .catch(() => setLoaded(true));
    }
  }, [courseId, module]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['.pdf', '.doc', '.docx', '.odt', '.txt', '.rtf'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowed.includes(ext)) {
      toast.error(`Format non accepte. Formats: ${allowed.join(', ')}`);
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 20 MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(
        `/foad/courses/${courseId}/modules/${module.module_id}/submit-devoir`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(r.data.message);
      // Reload submission
      const sub = await axios.get(`/foad/courses/${courseId}/modules/${module.module_id}/my-submission`);
      setSubmission(sub.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const dueDate = module.due_date;
  const endDate = module.end_date;
  const noteMax = module.note_passage || 10;
  const isLate = endDate && new Date(endDate) < new Date();

  return (
    <div className="space-y-4" data-testid="module-devoir">
      {/* Consignes */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Consignes du devoir
        </h4>
        {module.contenu ? (
          <div className="text-sm text-orange-700 prose max-w-none break-words overflow-hidden [&_img]:max-w-full [&_img]:h-auto" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }} dangerouslySetInnerHTML={{ __html: nettoyerHtml(module.contenu) }} />
        ) : (
          <p className="text-sm text-orange-600">Aucune consigne.</p>
        )}
        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-orange-600">
          {dueDate && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> Rendu attendu: {new Date(dueDate).toLocaleDateString('fr-FR')}
            </span>
          )}
          {endDate && (
            <span className={`flex items-center gap-1 ${isLate ? 'text-red-600 font-bold' : ''}`}>
              <Clock className="h-3 w-3" /> Date limite: {new Date(endDate).toLocaleDateString('fr-FR')}
              {isLate && ' (expiree)'}
            </span>
          )}
          <span>Note max: {noteMax}/10</span>
        </div>
      </div>

      {/* Submission status */}
      {loaded && submission ? (
        <div className={`border rounded-lg p-4 ${submission.status === 'graded' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`} data-testid="devoir-submission-status">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              {submission.status === 'graded' ? (
                <><CheckCircle className="h-4 w-4 text-green-600" /> Devoir corrige</>
              ) : (
                <><Clock className="h-4 w-4 text-blue-600" /> Devoir soumis - en attente de correction</>
              )}
            </h4>
          </div>
          <div className="text-sm text-slate-600 space-y-1">
            <p>Fichier: <span className="font-medium">{submission.filename}</span> ({(submission.file_size / 1024).toFixed(0)} Ko)</p>
            <p>Soumis le: {new Date(submission.submitted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          {submission.status === 'graded' && (
            <div className="mt-3 p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className={`text-2xl font-black ${submission.note >= (noteMax * 0.5) ? 'text-green-600' : 'text-red-600'}`}>{submission.note}/{submission.note_max || noteMax}</p>
                  <p className="text-xs text-slate-500">Note</p>
                </div>
                {submission.commentaire && (
                  <div className="flex-1 border-l pl-4">
                    <p className="text-xs font-medium text-slate-500 mb-1">Commentaire du formateur :</p>
                    <p className="text-sm text-slate-700">{submission.commentaire}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Resubmit option */}
          {submission.status !== 'graded' && (
            <div className="mt-3">
              <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf,.doc,.docx,.odt,.txt,.rtf" className="hidden" />
              <Button variant="outline" size="sm" className="text-blue-600 border-blue-200" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="resubmit-devoir-btn">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? 'Envoi...' : 'Redeposer un fichier'}
              </Button>
            </div>
          )}
        </div>
      ) : loaded ? (
        /* Upload form */
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center" data-testid="devoir-upload-form">
          <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf,.doc,.docx,.odt,.txt,.rtf" className="hidden" />
          <File className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600 mb-1">Deposez votre devoir</p>
          <p className="text-xs text-slate-400 mb-4">Formats acceptes: PDF, Word, ODT, TXT (max 20 MB)</p>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => fileRef.current?.click()} disabled={uploading || isLate} data-testid="upload-devoir-btn">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Envoi en cours...' : 'Choisir un fichier'}
          </Button>
          {isLate && <p className="text-xs text-red-500 mt-2">La date limite est depassee</p>}
        </div>
      ) : (
        <div className="text-center py-4"><div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>
      )}
    </div>
  );
};

export default FOADApprenantPage;
